import { useState, useEffect } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { ArrowUp } from 'lucide-react'
import VantaFog from '@/components/VantaFog'
import AmbientEffects from '@/components/AmbientEffects'
import Navbar from '@/components/Navbar'
import Home from '@/pages/Home'
import ConductPage from '@/pages/ConductPage'
import PrivacyPolicy from '@/pages/PrivacyPolicy'
import TermsOfService from '@/pages/TermsOfService'
import NewsListPage from '@/pages/NewsListPage'
import NewsDetailPage from '@/pages/NewsDetailPage'
import { useCommunity } from '@/useCommunity'
import { LocalizationRuntime, hubMeta, useLanguage } from '@/localization'

/**
 * Uliunai community hub — the public front door to every Uliunai game, and the
 * canonical home for community identity (story, Discord, leadership, conduct).
 * Routes: `/` (single-page scroll home), `/conduct` (full Code of Conduct), and
 * the platform-wide legal docs `/privacy` and `/terms` (every game site links
 * up to these rather than shipping its own copy).
 * A fixed Vanta fog + ambient embers sit behind all content.
 */
/** Appears after scrolling down; returns the user to the top. */
function ScrollToTopButton() {
  const [show, setShow] = useState(false)
  const { t } = useLanguage()

  useEffect(() => {
    const onScroll = () => setShow(window.scrollY > 400)
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  if (!show) return null

  return (
    <button
      onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
      className="fixed bottom-6 right-6 z-50 flex h-11 w-11 cursor-pointer items-center justify-center rounded-full bg-uliunai-red text-white shadow-card transition-all hover:-translate-y-0.5 hover:bg-uliunai-red-dark"
      aria-label={t('Scroll to top')}
    >
      <ArrowUp size={20} />
    </button>
  )
}

export default function App() {
  const community = useCommunity()

  return (
    <div className="relative min-h-screen bg-parchment text-stone-700">
      {/* Fixed atmospheric smoke (z-0) + drifting embers (z-1) behind all content;
          page content sits at z-10. */}
      <VantaFog style={{ position: 'fixed' }} />
      <AmbientEffects />

      <div className="relative z-10">
        <LocalizationRuntime meta={hubMeta} />
        <Navbar community={community} />
        <Routes>
          <Route path="/" element={<Home community={community} />} />
          <Route
            path="/conduct"
            element={<ConductPage conduct={community.conduct} social={community.social} />}
          />
          <Route path="/privacy" element={<PrivacyPolicy social={community.social} />} />
          <Route path="/terms" element={<TermsOfService social={community.social} />} />
          <Route path="/news" element={<NewsListPage social={community.social} />} />
          <Route path="/news/:id" element={<NewsDetailPage social={community.social} />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <ScrollToTopButton />
      </div>
    </div>
  )
}
